import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Flex, Text, Button, Avatar, VStack, HStack, SimpleGrid, useToast, Badge, IconButton, Tooltip
} from '@chakra-ui/react';
import { 
  MdFiberManualRecord, MdStop, MdPause, MdPlayArrow, MdFullscreen, MdPeople, 
  MdMic, MdMicOff, MdVideocam, MdVideocamOff, MdScreenShare, MdStopScreenShare, MdCallEnd
} from 'react-icons/md';
import { useTracks, useParticipants, useLocalParticipant, VideoTrack } from '@livekit/components-react';
import { Track } from 'livekit-client';
import axiosInstance from '../services/axiosInstance';

// Individual Participant Video Card component
function ParticipantCard({ participant, cameraTrackRef, onMute, onKick, isInstructorOrAdmin }) {
  const isSpeaking = participant?.isSpeaking;
  const isMicEnabled = participant?.isMicrophoneEnabled !== false;
  const isCamEnabled = participant?.isCameraEnabled !== false && cameraTrackRef?.publication?.track && !cameraTrackRef?.publication?.isMuted;

  let role = "student";
  try {
    if (participant?.metadata) {
      const meta = JSON.parse(participant.metadata);
      if (meta.role) role = meta.role;
    }
  } catch(e) {}

  return (
    <Box
      position="relative"
      borderRadius="2xl"
      overflow="hidden"
      bg="#0f172a"
      border="2px solid"
      borderColor={isSpeaking ? "blue.400" : "rgba(255, 255, 255, 0.12)"}
      boxShadow={isSpeaking ? "0 0 24px rgba(59, 130, 246, 0.5)" : "0 10px 30px rgba(0, 0, 0, 0.4)"}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      h="100%"
      minH={{ base: "180px", md: "240px" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      _hover={{ transform: "translateY(-4px)", borderColor: "blue.300", boxShadow: "0 14px 35px rgba(0, 0, 0, 0.5)" }}
    >
      {/* Video Stream or Avatar Fallback */}
      {isCamEnabled && cameraTrackRef ? (
        <Box w="100%" h="100%" position="absolute" top={0} left={0} zIndex={1}>
          <VideoTrack trackRef={cameraTrackRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      ) : (
        <VStack spacing={3} zIndex={2} p={4} textAlign="center">
          <Box position="relative">
            <Avatar 
              size="xl" 
              name={participant.name || "طالب"} 
              bg={role === 'instructor' || role === 'admin' ? "blue.600" : "purple.600"}
              color="white"
              boxShadow="2xl"
            />
            {isSpeaking && (
              <Box 
                position="absolute" 
                top="-8px" left="-8px" right="-8px" bottom="-8px" 
                borderRadius="full" 
                border="3px solid #3b82f6" 
                animation="pulse 1.5s infinite"
                sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }}
              />
            )}
          </Box>
        </VStack>
      )}

      {/* Top Overlay Badges */}
      <Flex position="absolute" top={3} left={3} right={3} justify="space-between" align="center" zIndex={10} pointerEvents="auto">
        <HStack spacing={1.5} bg="rgba(15, 23, 42, 0.75)" px={2.5} py={1} borderRadius="xl" backdropFilter="blur(8px)" border="1px solid rgba(255,255,255,0.1)">
          {isMicEnabled ? (
            <MdMic color="#4ade80" size={16} />
          ) : (
            <MdMicOff color="#f87171" size={16} />
          )}
          <Badge fontSize="10px" colorScheme={role === 'instructor' || role === 'admin' ? 'purple' : 'blue'} variant="subtle" borderRadius="md" px={1.5}>
            {role === 'instructor' ? 'أستاذ' : role === 'admin' ? 'مشرف' : 'طالب'}
          </Badge>
        </HStack>

        {isInstructorOrAdmin && !participant.isLocal && role === 'student' && (
          <HStack spacing={1} bg="rgba(15, 23, 42, 0.75)" p={1} borderRadius="xl" backdropFilter="blur(8px)" border="1px solid rgba(255,255,255,0.1)">
            <Tooltip label="كتم الميكروفون" hasArrow>
              <IconButton
                icon={<MdMicOff />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                borderRadius="md"
                onClick={() => onMute(participant)}
                aria-label="Mute"
              />
            </Tooltip>
            <Tooltip label="طرد من الحصة" hasArrow>
              <IconButton
                icon={<MdStop />}
                size="xs"
                colorScheme="orange"
                variant="ghost"
                borderRadius="md"
                onClick={() => onKick(participant, false)}
                aria-label="Kick"
              />
            </Tooltip>
          </HStack>
        )}
      </Flex>

      {/* Bottom Participant Name Label */}
      <Box 
        position="absolute" 
        bottom={3} 
        right={3} 
        zIndex={10} 
        bg="rgba(15, 23, 42, 0.85)" 
        backdropFilter="blur(10px)" 
        px={3} 
        py={1} 
        borderRadius="xl" 
        border="1px solid rgba(255,255,255,0.1)"
        maxW="80%"
      >
        <Text color="white" fontSize="xs" fontWeight="bold" dir="rtl" isTruncated>
          {participant.name || "طالب"} {participant.isLocal ? "(أنت)" : ""}
        </Text>
      </Box>
    </Box>
  );
}

// Compact Participant List Item component (for Sidebar / List View)
function ParticipantListItem({ participant, cameraTrackRef, onMute, onKick, isInstructorOrAdmin }) {
  const isSpeaking = participant?.isSpeaking;
  const isMicEnabled = participant?.isMicrophoneEnabled !== false;
  const isCamEnabled = participant?.isCameraEnabled !== false && cameraTrackRef?.publication?.track && !cameraTrackRef?.publication?.isMuted;

  let role = "student";
  try {
    if (participant?.metadata) {
      const meta = JSON.parse(participant.metadata);
      if (meta.role) role = meta.role;
    }
  } catch(e) {}

  return (
    <Flex
      align="center"
      justify="space-between"
      p={3}
      borderRadius="xl"
      bg="rgba(15, 23, 42, 0.85)"
      border="1.5px solid"
      borderColor={isSpeaking ? "blue.400" : "rgba(255, 255, 255, 0.1)"}
      boxShadow={isSpeaking ? "0 0 16px rgba(59, 130, 246, 0.4)" : "0 4px 12px rgba(0, 0, 0, 0.3)"}
      backdropFilter="blur(10px)"
      transition="all 0.2s ease"
      dir="rtl"
      w="100%"
      _hover={{ bg: "rgba(30, 41, 59, 0.9)", borderColor: "blue.300" }}
    >
      <HStack spacing={3} overflow="hidden">
        {/* Video Thumbnail or Avatar */}
        <Box position="relative" w="42px" h="42px" borderRadius="lg" overflow="hidden" flexShrink={0} bg="gray.800">
          {isCamEnabled && cameraTrackRef ? (
            <VideoTrack trackRef={cameraTrackRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Avatar 
              size="sm" 
              name={participant.name || "طالب"} 
              bg={role === 'instructor' || role === 'admin' ? "blue.600" : "purple.600"}
              color="white"
              w="100%" h="100%"
            />
          )}
          {isSpeaking && (
            <Box 
              position="absolute" 
              top="0" left="0" right="0" bottom="0" 
              borderRadius="lg" 
              border="2px solid #3b82f6" 
              animation="pulse 1.5s infinite"
            />
          )}
        </Box>

        {/* Participant Name & Role */}
        <VStack align="start" spacing={0} overflow="hidden">
          <Text color="white" fontSize="sm" fontWeight="bold" isTruncated>
            {participant.name || "طالب"} {participant.isLocal ? "(أنت)" : ""}
          </Text>
          <Badge fontSize="9px" colorScheme={role === 'instructor' || role === 'admin' ? 'purple' : 'blue'} variant="subtle" borderRadius="md" px={1.5}>
            {role === 'instructor' ? 'أستاذ' : role === 'admin' ? 'مشرف' : 'طالب'}
          </Badge>
        </VStack>
      </HStack>

      {/* Mic Status & Actions */}
      <HStack spacing={2} flexShrink={0}>
        <Box p={1.5} borderRadius="lg" bg="rgba(0,0,0,0.4)">
          {isMicEnabled ? (
            <MdMic color="#4ade80" size={18} />
          ) : (
            <MdMicOff color="#f87171" size={18} />
          )}
        </Box>

        {isInstructorOrAdmin && !participant.isLocal && role === 'student' && (
          <HStack spacing={1}>
            <Tooltip label="كتم الميكروفون" hasArrow>
              <IconButton
                icon={<MdMicOff />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                borderRadius="md"
                onClick={() => onMute(participant)}
                aria-label="Mute"
              />
            </Tooltip>
            <Tooltip label="طرد" hasArrow>
              <IconButton
                icon={<MdStop />}
                size="xs"
                colorScheme="orange"
                variant="ghost"
                borderRadius="md"
                onClick={() => onKick(participant, false)}
                aria-label="Kick"
              />
            </Tooltip>
          </HStack>
        )}
      </HStack>
    </Flex>
  );
}

export default function CustomMeetingLayout({ socket, roomName, secretKey, user }) {
  const toast = useToast();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isLocalRecording, setIsLocalRecording] = useState(false);
  const [isLocalRecordingPaused, setIsLocalRecordingPaused] = useState(false);

  const mediaRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksRef = useRef([]);

  // LiveKit Track Hooks
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  const screenTracks = useTracks([{ source: Track.Source.ScreenShare }], { onlySubscribed: false });
  const hasScreenShare = screenTracks && screenTracks.length > 0;

  // Local Participant controls
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();

  const participants = useParticipants();
  const participantCount = participants.length;

  const isInstructorOrAdmin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'instructor';

  const handleToggleMic = async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    }
  };

  const handleToggleCamera = async () => {
    if (localParticipant) {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    }
  };

  const handleToggleScreenShare = async () => {
    if (localParticipant) {
      await localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
    }
  };

  const handleFullscreenShare = () => {
    const target = document.querySelector('.screen-share-card-container video');
    if (target) {
      if (!document.fullscreenElement) {
        target.requestFullscreen().catch(err => console.error(err));
      } else {
        document.exitFullscreen().catch(err => console.error(err));
      }
    } else {
      toast({ title: "لم يتم العثور على شاشة مشاركة لتكبيرها", status: "warning", duration: 2000 });
    }
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("admission_request_received", (data) => {
      setPendingRequests(prev => [...prev, data]);
      toast({ title: "طلب انضمام جديد", status: "info", duration: 3000, position: "top-right", isClosable: true });
    });

    socket.on("admission_handled", (data) => {
      setPendingRequests(prev => prev.filter(req => req.socketId !== data.targetSocketId));
    });

    socket.on("room_state_update", (state) => {
      setIsRecording(state.isRecording);
    });

    socket.on("recording_state_changed", (state) => {
      setIsRecording(state.isRecording);
      toast({ 
        title: state.isRecording ? "بدأ التسجيل" : "توقف التسجيل", 
        status: state.isRecording ? "success" : "warning", 
        duration: 2000,
        position: "top-right"
      });
    });

    return () => {
      socket.off("admission_request_received");
      socket.off("admission_handled");
      socket.off("room_state_update");
      socket.off("recording_state_changed");
    };
  }, [socket, toast]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleKick = async (participant, block = false) => {
    try {
      const response = await axiosInstance.post('/meetings/kick', {
        roomName,
        identity: participant.identity,
        block
      });
      if (response.data.success) {
        toast({ title: response.data.message || "تم العملية بنجاح", status: "success", duration: 2000, position: "top-right" });
      }
    } catch (err) {
      toast({ title: err.response?.data?.error || "حدث خطأ", status: "error", duration: 3000, position: "top-right" });
    }
  };

  const handleMuteMic = async (participant) => {
    try {
      const response = await axiosInstance.post('/meetings/mute', {
        roomName,
        identity: participant.identity,
        muted: true
      });
      if (response.data.success) {
        toast({ 
          title: response.data.message || "تم كتم الميكروفون بنجاح", 
          status: "success", 
          duration: 2000, 
          position: "top-right" 
        });
      }
    } catch (err) {
      toast({ 
        title: err.response?.data?.error || "حدث خطأ في كتم الصوت", 
        status: "error", 
        duration: 3000, 
        position: "top-right" 
      });
    }
  };

  const handleMuteAllStudents = async () => {
    const studentParticipants = participants.filter(p => {
      if (p.isLocal) return false;
      let role = "student";
      try {
        if (p.metadata) {
          const meta = JSON.parse(p.metadata);
          if (meta.role) role = meta.role;
        }
      } catch (e) {}
      return role === 'student';
    });

    if (studentParticipants.length === 0) {
      toast({ title: "لا يوجد طلاب في الحصة حالياً لكتم صوتهم", status: "info", duration: 2000, position: "top-right" });
      return;
    }

    try {
      await Promise.all(
        studentParticipants.map(p => 
          axiosInstance.post('/meetings/mute', { roomName, identity: p.identity, muted: true })
        )
      );
      toast({ title: "تم كتم ميكروفونات جميع الطلاب بنجاح", status: "success", duration: 3000, position: "top-right" });
    } catch (err) {
      toast({ title: "حدث خطأ أثناء كتم صوت بعض الطلاب", status: "error", duration: 3000, position: "top-right" });
    }
  };

  const handleEndMeetingForEveryone = async () => {
    if (window.confirm("هل أنت تأكد من إغلاق وإنهاء الحصة للجميع؟")) {
      try {
        await axiosInstance.post(`/meetings/end/${encodeURIComponent(roomName)}`);
        toast({
          title: "تم إنهاء الحصة للجميع بنجاح",
          status: "success",
          duration: 3000,
          position: "top-right"
        });
      } catch (err) {
        toast({
          title: "خطأ في إنهاء الحصة",
          description: err.response?.data?.error || err.message,
          status: "error",
          duration: 3000,
          position: "top-right"
        });
      }
    }
  };

  const handleAdmit = (req, approved) => {
    socket.emit("handle_admission", { roomName, targetSocketId: req.socketId, approved, secretKey });
    setPendingRequests(prev => prev.filter(r => r.socketId !== req.socketId));
  };

  const startRecording = async () => {
    let screenStream = null;
    let micStream = null;
    let audioContext = null;

    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always" },
        audio: true
      });
    } catch (err) {
      console.error("Error capturing screen:", err);
      toast({
        title: "تعذر بدء تسجيل الشاشة",
        description: "يرجى منح الإذن لمشاركة الشاشة لتسجيل الحصة.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    try {
      const audioTracks = [];
      const screenAudioTracks = screenStream.getAudioTracks();

      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr) {
        console.warn("No mic access or mic not available:", micErr);
      }

      const micAudioTracks = micStream ? micStream.getAudioTracks() : [];

      if (screenAudioTracks.length > 0 || micAudioTracks.length > 0) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const destination = audioContext.createMediaStreamDestination();

        if (screenAudioTracks.length > 0) {
          const screenSource = audioContext.createMediaStreamSource(new MediaStream([screenAudioTracks[0]]));
          screenSource.connect(destination);
        }

        if (micAudioTracks.length > 0) {
          const micSource = audioContext.createMediaStreamSource(new MediaStream([micAudioTracks[0]]));
          micSource.connect(destination);
        }

        audioTracks.push(destination.stream.getAudioTracks()[0]);
      }

      const videoTrack = screenStream.getVideoTracks()[0];
      const combinedStream = new MediaStream([videoTrack, ...audioTracks]);

      screenStreamRef.current = screenStream;
      micStreamRef.current = micStream;
      audioContextRef.current = audioContext;

      chunksRef.current = [];

      let options = { mimeType: 'video/webm;codecs=vp9,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm;codecs=vp8,opus' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/mp4' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = {};
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = url;
        const dateStr = new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/:/g, '-');
        a.download = `تسجيل_حصة_${roomName || 'meeting'}_${dateStr}.${mediaRecorder.mimeType?.includes('mp4') ? 'mp4' : 'webm'}`;
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);

        toast({
          title: "تم حفظ التسجيل محلياً",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right"
        });
      };

      videoTrack.onended = () => {
        stopRecording();
      };

      mediaRecorder.start(1000);
      setIsLocalRecording(true);

      if (socket) {
        socket.emit("toggle_recording", { roomName, isRecording: true });
      }

      toast({
        title: "بدأ تسجيل الشاشة محلياً",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });

    } catch (recorderErr) {
      console.error("Error setting up recorder:", recorderErr);
      toast({
        title: "فشل بدء التسجيل",
        description: recorderErr.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      if (screenStream) screenStream.getTracks().forEach(t => t.stop());
      if (micStream) micStream.getTracks().forEach(t => t.stop());
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsLocalRecording(false);
    setIsLocalRecordingPaused(false);

    if (socket) {
      socket.emit("toggle_recording", { roomName, isRecording: false });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsLocalRecordingPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsLocalRecordingPaused(false);
    }
  };

  const handlePauseResumeRecording = () => {
    if (isLocalRecordingPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const handleToggleRecording = () => {
    if (isLocalRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Determine participant column grid size
  const gridColumns = {
    base: 1,
    sm: participantCount > 1 ? 2 : 1,
    md: participantCount > 4 ? 3 : participantCount > 1 ? 2 : 1,
    lg: participantCount > 6 ? 4 : participantCount > 2 ? 3 : participantCount > 1 ? 2 : 1
  };

  return (
    <Box 
      className="my-custom-meeting-layout" 
      position="absolute" 
      top={0} left={0} right={0} bottom={0} 
      bg="#090d16" 
      display="flex" 
      flexDirection="column" 
      fontFamily="Tajawal, sans-serif" 
      zIndex={50}
      overflow="hidden"
    >
      {/* 1. TOP HEADER BAR */}
      <Flex 
        w="100%" 
        h="60px" 
        bg="rgba(15, 23, 42, 0.9)" 
        px={6} 
        align="center" 
        justify="space-between" 
        borderBottom="1px solid rgba(255,255,255,0.1)"
        backdropFilter="blur(12px)"
        zIndex={100}
      >
        <HStack spacing={3}>
          <Badge colorScheme="blue" variant="solid" px={3} py={1} borderRadius="lg" fontSize="xs" fontWeight="bold">
            {roomName}
          </Badge>
          {isRecording && (
            <HStack spacing={1.5} bg="red.500" color="white" px={2.5} py={0.5} borderRadius="full">
              <Box w={2} h={2} bg="white" borderRadius="full" className="pulse-animation" />
              <Text fontSize="11px" fontWeight="bold">التسجيل جارٍ</Text>
            </HStack>
          )}
        </HStack>

        <HStack spacing={3}>
          {isInstructorOrAdmin && (
            <HStack spacing={2} bg="rgba(255,255,255,0.05)" px={3} py={1} borderRadius="xl">
              <IconButton 
                icon={isLocalRecording ? <MdStop /> : <MdFiberManualRecord />} 
                size="xs"
                colorScheme={isLocalRecording ? "red" : "gray"}
                variant="solid"
                onClick={handleToggleRecording}
                aria-label="Toggle Recording"
                isRound
              />
              {isLocalRecording && (
                <IconButton
                  icon={isLocalRecordingPaused ? <MdPlayArrow /> : <MdPause />}
                  size="xs"
                  colorScheme="gray"
                  variant="ghost"
                  color="white"
                  onClick={handlePauseResumeRecording}
                  aria-label="Pause Recording"
                  isRound
                />
              )}
              <Text fontSize="xs" color="gray.300" fontWeight="medium">
                {isLocalRecording ? (isLocalRecordingPaused ? "مؤقت" : "تسجيل محلي") : "تسجيل"}
              </Text>
            </HStack>
          )}

          {isInstructorOrAdmin && (
            <Button 
              size="xs" 
              colorScheme="red" 
              variant="solid" 
              onClick={handleEndMeetingForEveryone}
              borderRadius="lg"
              px={3}
            >
              إنهاء للجميع
            </Button>
          )}
        </HStack>
      </Flex>

      {/* 2. MAIN CONTENT AREA (CARDS GRID & SCREEN SHARE FOCUS CARD) */}
      <Flex flex={1} w="100%" overflow="hidden" position="relative" p={4} gap={4} direction={{ base: "column", lg: hasScreenShare ? "row" : "column" }}>
        
        {/* Screen Share Card (if present) */}
        {hasScreenShare && (
          <Box 
            flex={3} 
            className="screen-share-card-container"
            position="relative" 
            borderRadius="2xl" 
            overflow="hidden" 
            bg="#0f172a" 
            border="2px solid rgba(59, 130, 246, 0.4)" 
            boxShadow="0 10px 30px rgba(0,0,0,0.5)"
          >
            <VideoTrack trackRef={screenTracks[0]} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            <Flex position="absolute" top={3} right={3} gap={2} zIndex={20}>
              <Button 
                leftIcon={<MdFullscreen />} 
                size="xs" 
                colorScheme="blue" 
                onClick={handleFullscreenShare}
                borderRadius="lg"
              >
                تكبير
              </Button>
            </Flex>
            <Box position="absolute" bottom={3} right={3} bg="rgba(0,0,0,0.7)" px={3} py={1} borderRadius="lg" zIndex={20}>
              <Text fontSize="xs" color="white" fontWeight="bold">شاشة مشاركة</Text>
            </Box>
          </Box>
        )}

        {/* Participant Sidebar / Area (List View when screen share active) */}
        <Box 
          flex={hasScreenShare ? 1 : 1} 
          w="100%" 
          maxW={hasScreenShare ? { base: "100%", lg: "340px" } : "100%"}
          h="100%" 
          overflowY="auto" 
          pr={1}
          sx={{ '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.2)', borderRadius: '4px' } }}
        >
          {hasScreenShare ? (
            <VStack spacing={2.5} align="stretch" w="100%">
              {participants.map((p) => {
                const camTrack = cameraTracks.find(t => t.participant?.identity === p.identity);

                return (
                  <ParticipantListItem
                    key={p.identity}
                    participant={p}
                    cameraTrackRef={camTrack}
                    onMute={handleMuteMic}
                    onKick={handleKick}
                    isInstructorOrAdmin={isInstructorOrAdmin}
                  />
                );
              })}
            </VStack>
          ) : (
            <SimpleGrid columns={gridColumns} spacing={4} h="100%">
              {participants.map((p) => {
                const camTrack = cameraTracks.find(t => t.participant?.identity === p.identity);

                return (
                  <ParticipantCard
                    key={p.identity}
                    participant={p}
                    cameraTrackRef={camTrack}
                    onMute={handleMuteMic}
                    onKick={handleKick}
                    isInstructorOrAdmin={isInstructorOrAdmin}
                  />
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      </Flex>

      {/* 3. FLOATING CARDS CONTROL BAR AT BOTTOM */}
      <Flex justify="center" w="100%" p={4} position="absolute" bottom={0} left={0} right={0} zIndex={100} pointerEvents="none">
        <HStack 
          spacing={3} 
          bg="rgba(15, 23, 42, 0.9)" 
          px={6} 
          py={3} 
          borderRadius="2xl" 
          border="1px solid rgba(255,255,255,0.15)" 
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.6)" 
          backdropFilter="blur(16px)"
          pointerEvents="auto"
          dir="rtl"
        >
          {/* Mic Button */}
          <Tooltip label={isMicrophoneEnabled ? "كتم الميكروفون" : "تشغيل الميكروفون"} hasArrow>
            <IconButton
              icon={isMicrophoneEnabled ? <MdMic /> : <MdMicOff />}
              onClick={handleToggleMic}
              colorScheme={isMicrophoneEnabled ? "gray" : "red"}
              variant={isMicrophoneEnabled ? "solid" : "solid"}
              borderRadius="xl"
              size="lg"
              aria-label="Toggle Mic"
            />
          </Tooltip>

          {/* Camera Button */}
          <Tooltip label={isCameraEnabled ? "إيقاف الكاميرا" : "تشغيل الكاميرا"} hasArrow>
            <IconButton
              icon={isCameraEnabled ? <MdVideocam /> : <MdVideocamOff />}
              onClick={handleToggleCamera}
              colorScheme={isCameraEnabled ? "gray" : "red"}
              variant={isCameraEnabled ? "solid" : "solid"}
              borderRadius="xl"
              size="lg"
              aria-label="Toggle Camera"
            />
          </Tooltip>

          {/* Screen Share Button */}
          <Tooltip label={isScreenShareEnabled ? "إيقاف مشاركة الشاشة" : "مشاركة الشاشة"} hasArrow>
            <IconButton
              icon={isScreenShareEnabled ? <MdStopScreenShare /> : <MdScreenShare />}
              onClick={handleToggleScreenShare}
              colorScheme={isScreenShareEnabled ? "green" : "gray"}
              variant="solid"
              borderRadius="xl"
              size="lg"
              aria-label="Toggle Screen Share"
            />
          </Tooltip>

          {/* Participants List Toggle Button */}
          {isInstructorOrAdmin && (
            <Tooltip label="المشاركون في الحصة" hasArrow>
              <Button
                leftIcon={<MdPeople />}
                onClick={() => setShowParticipants(!showParticipants)}
                colorScheme={showParticipants ? "blue" : "gray"}
                variant="solid"
                borderRadius="xl"
                size="lg"
                px={4}
              >
                {participantCount}
              </Button>
            </Tooltip>
          )}

          {/* Mute All Students Button */}
          {isInstructorOrAdmin && (
            <Tooltip label="كتم جميع الطلاب" hasArrow>
              <Button
                colorScheme="orange"
                variant="subtle"
                onClick={handleMuteAllStudents}
                borderRadius="xl"
                size="lg"
                px={4}
                fontSize="xs"
              >
                كتم الجميع
              </Button>
            </Tooltip>
          )}

          {/* Leave Button */}
          <Button
            leftIcon={<MdCallEnd />}
            colorScheme="red"
            variant="solid"
            borderRadius="xl"
            size="lg"
            px={6}
            fontWeight="bold"
            onClick={() => {
              if (window.confirm("هل تريد مغادرة الحصة؟")) {
                window.history.back();
              }
            }}
          >
            مغادرة
          </Button>
        </HStack>
      </Flex>

      {/* 4. WAITING ROOM CARD OVERLAY */}
      {isInstructorOrAdmin && pendingRequests.length > 0 && (
        <Box position="absolute" top={16} right={4} zIndex={120} w="320px" pointerEvents="auto">
          <Box bg="white" borderRadius="2xl" shadow="2xl" overflow="hidden" border="1px solid #e2e8f0">
            <Flex bg="#f8fafc" p={3.5} borderBottom="1px solid #e2e8f0" justify="space-between" align="center">
              <Text fontSize="13px" color="#0f172a" fontWeight="bold">
                غرفة الانتظار (Waiting Room)
              </Text>
              <Badge colorScheme="blue" borderRadius="full">{pendingRequests.length}</Badge>
            </Flex>
            <VStack maxH="280px" overflowY="auto" p={0} spacing={0} bg="white">
              {pendingRequests.map((req, idx) => (
                <Flex key={idx} w="full" align="center" justify="space-between" p={3} borderBottom="1px solid #f1f5f9">
                  <HStack spacing={2}>
                    <Avatar size="sm" name={req.user?.username || "طالب"} />
                    <Text fontSize="13px" color="#0f172a" fontWeight="600">{req.user?.username || "طالب"}</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Button size="xs" colorScheme="blue" borderRadius="lg" px={3} onClick={() => handleAdmit(req, true)}>قبول</Button>
                    <Button size="xs" colorScheme="gray" variant="ghost" borderRadius="lg" onClick={() => handleAdmit(req, false)}>رفض</Button>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        </Box>
      )}

      {/* 5. PARTICIPANTS MANAGEMENT SIDEBAR CARD */}
      {isInstructorOrAdmin && showParticipants && (
        <Box position="absolute" top={16} right={4} zIndex={120} w="360px" pointerEvents="auto">
          <Box 
            bg="rgba(255, 255, 255, 0.96)" 
            backdropFilter="blur(16px)"
            borderRadius="2xl" 
            shadow="2xl" 
            overflow="hidden" 
            border="1px solid rgba(226, 232, 240, 0.9)"
          >
            <Flex bg="#f8fafc" p={4} borderBottom="1px solid #e2e8f0" justify="space-between" align="center">
              <HStack spacing={2}>
                <Text fontSize="14px" color="#0f172a" fontWeight="bold">
                  المشاركون في الحصة
                </Text>
                <Badge colorScheme="blue" borderRadius="full" px={2.5} py={0.5}>{participantCount}</Badge>
              </HStack>
              <HStack spacing={2}>
                <Button size="xs" colorScheme="blue" borderRadius="lg" onClick={handleMuteAllStudents}>كتم الجميع</Button>
                <Button size="xs" variant="ghost" borderRadius="lg" onClick={() => setShowParticipants(false)}>✕</Button>
              </HStack>
            </Flex>
            <VStack maxH="calc(100vh - 220px)" overflowY="auto" p={3} spacing={2.5} bg="white">
              {participants.map((p) => {
                let role = "student";
                try {
                  if (p.metadata) {
                    const meta = JSON.parse(p.metadata);
                    if (meta.role) role = meta.role;
                  }
                } catch(e) {}
                const isSelf = p.isLocal;
                
                return (
                  <Flex key={p.identity} w="full" align="center" justify="space-between" p={3} borderRadius="xl" border="1px solid #f1f5f9" bg="#fafafa">
                    <HStack spacing={3}>
                      <Avatar size="sm" name={p.name || "طالب"} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="13px" color="#0f172a" fontWeight="600">{p.name || "طالب"} {isSelf ? "(أنت)" : ""}</Text>
                        <Badge fontSize="9px" colorScheme={role === 'instructor' || role === 'admin' ? 'purple' : 'blue'}>{role}</Badge>
                      </VStack>
                    </HStack>
                    {!isSelf && role === 'student' && (
                      <HStack spacing={1}>
                        <Button size="xs" colorScheme="blue" borderRadius="md" px={2} onClick={() => handleMuteMic(p)}>كتم</Button>
                        <Button size="xs" colorScheme="orange" borderRadius="md" px={2} onClick={() => handleKick(p, false)}>طرد</Button>
                        <Button size="xs" colorScheme="red" borderRadius="md" px={2} onClick={() => handleKick(p, true)}>حظر</Button>
                      </HStack>
                    )}
                  </Flex>
                )
              })}
            </VStack>
          </Box>
        </Box>
      )}
    </Box>
  );
}

